/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — chart legend, single source of truth.

   Josh grant 2026-08-18: "needs a LEGEND identifying what every
   badge/abbreviation on the chart means (MP and M and blah blah all
   the badges)." Pure computation only — reads directly off each
   `person` node's OWN `PersonNodeData` (the exact same fields
   `PersonNode` in `nodes.tsx` reads to render the badges themselves),
   so there is no hand-maintained duplicate list to drift out of sync.
   Both the on-canvas `LegendPopover` and the print/export
   `CaptureSheet` call this SAME function against the SAME node array.

   Scope, deliberately narrow (per the grant — "compact, unobtrusive",
   "only show entries actually present"): the star/title-badge/
   department-pill glyphs are short codes that need translating. Team
   pills already show the team's full name (self-explanatory, no
   abbreviation to decode) and are left out to keep the legend short;
   the print sheet's existing per-team color strip already covers
   that. The inactive-member dim state is included since it's a
   genuine visual code with no text label anywhere on the card.
   ═══════════════════════════════════════════════════════════════ */

import type { Node } from '@xyflow/react';
import type { PersonNodeData } from './types';

export interface LegendEntry {
  /** Stable key for React lists / de-dup. */
  key: string;
  /** Short glyph/code as it appears on the card (e.g. "MP", "★"). */
  code: string;
  color: string;
  /** Plain-English meaning. */
  label: string;
}

export interface ChartLegend {
  leader: LegendEntry | null;
  titleBadges: LegendEntry[];
  departmentPills: LegendEntry[];
  inactive: LegendEntry | null;
}

export function isChartLegendEmpty(legend: ChartLegend): boolean {
  return (
    !legend.leader &&
    legend.titleBadges.length === 0 &&
    legend.departmentPills.length === 0 &&
    !legend.inactive
  );
}

/** Scans the CURRENT set of rendered `person` nodes (whatever's on the
 *  board right now — Main or a custom view, already filtered by the
 *  caller) and returns only the badge kinds actually present, in the
 *  same on/off state each card is currently rendering them in
 *  (`nodeStyle`, embedded per-node by `buildPersonNode`). */
export function computeChartLegend(nodes: Node[]): ChartLegend {
  let leaderPresent = false;
  let inactivePresent = false;
  const titleBadgeMap = new Map<string, LegendEntry>();
  const deptPillMap = new Map<string, LegendEntry>();

  for (const n of nodes) {
    if (n.type !== 'person') continue;
    const d = n.data as PersonNodeData | undefined;
    if (!d) continue;
    const style = d.nodeStyle;

    if (d.isLeader && style.showLeaderStar) leaderPresent = true;
    if (d.member.active === false) inactivePresent = true;

    if (style.showTitleBadge && d.companyBadge) {
      const key = `${d.companyBadge.code}::${d.companyBadge.color}`;
      if (!titleBadgeMap.has(key)) {
        titleBadgeMap.set(key, {
          key,
          code: d.companyBadge.code,
          color: d.companyBadge.color,
          // Title's own full name (e.g. "Managing Principal" for "MP")
          // — NOT `roleLabel`, which is the person's PERMISSION role
          // (User/Admin/Site Admin), a different, unrelated field that
          // happens to also live on this same node.
          label: d.companyBadge.name || d.companyBadge.code,
        });
      }
    }

    if (style.showDepartmentPill) {
      for (const p of d.pills) {
        if (p.kind !== 'department') continue;
        // Only entries that are ACTUALLY shortened (label !== the
        // department's real name) need a legend line — a department
        // with no abbreviation set just shows its own full name on
        // the card already, nothing to decode.
        if (!p.fullLabel || p.fullLabel === p.label) continue;
        const key = `${p.label}::${p.color}`;
        if (!deptPillMap.has(key)) {
          deptPillMap.set(key, { key, code: p.label, color: p.color, label: p.fullLabel });
        }
      }
    }
  }

  return {
    leader: leaderPresent
      ? { key: 'leader', code: '★', color: 'var(--ac)', label: 'Team leader' }
      : null,
    titleBadges: [...titleBadgeMap.values()].sort((a, b) => a.code.localeCompare(b.code)),
    departmentPills: [...deptPillMap.values()].sort((a, b) => a.code.localeCompare(b.code)),
    inactive: inactivePresent
      ? { key: 'inactive', code: '', color: 'var(--tm)', label: 'Dimmed = archived member' }
      : null,
  };
}
